import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Input from '../components/input/Input';
import DatePicker from '../components/datepicker/DatePicker';
import DropDown from '../components/dropdown/DropDown';
import Button from '../components/button/Button';
import Spinner from '../components/Spinner/spinner';
import { 
  getCapitalContributionFormMetadata, 
  createCapitalContribution 
} from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';

interface MetadataItem {
  id: number;
  name: string;
}

interface CapitalContributionFormMetadata {
  protectors: MetadataItem[];
  currencies: MetadataItem[];
  funds: MetadataItem[];
  previousContributions: MetadataItem[];
}

interface FormValues {
  protector_id: number | null;
  contribution_date: string;
  expiration_date: string;
  currency_id: number | null;
  amount: string;
  fixed_available_amount: string;
  participant_shareholders_return: boolean;
  is_tax_deductible: boolean;
  authorized_fund_id: number | null;
  previous_contribution_id: number | null;
  minute_date: string;
  minute_number: string;
}

const ProtectorContributionNew = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [metadata, setMetadata] = useState<CapitalContributionFormMetadata | null>(null);

  const handleGoBack = () => {
    if (id) {
      navigate(`/protectores/${id}`);
    } else {
      navigate(routes.fundsAndProtectors);
    }
  };
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const twoYearsLater = new Date();
  twoYearsLater.setFullYear(twoYearsLater.getFullYear() + 2);
  const expirationDate = twoYearsLater.toISOString().split('T')[0];

  const [formValues, setFormValues] = useState<FormValues>({
    protector_id: id ? parseInt(id) : null,
    contribution_date: today,
    expiration_date: expirationDate,
    currency_id: null,
    amount: '',
    fixed_available_amount: '',
    participant_shareholders_return: false,
    is_tax_deductible: false,
    authorized_fund_id: null,
    previous_contribution_id: null,
    minute_date: today,
    minute_number: ''
  });

  useEffect(() => {
    fetchFormMetadata();
  }, []);

  const fetchFormMetadata = async () => {
    setLoading(true);
    try {
      const response = await getCapitalContributionFormMetadata();
      if (response.success && 'data' in response) {
        setMetadata(response.data);
      }
    } catch (error) {
      console.error('Error fetching form metadata:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFormValues = (updates: Partial<FormValues>) => {
    setFormValues(prev => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    if (formValues.contribution_date) {
      const contributionDate = new Date(formValues.contribution_date);
      contributionDate.setFullYear(contributionDate.getFullYear() + 2);
      const newExpirationDate = contributionDate.toISOString().split('T')[0];
      if (formValues.expiration_date !== newExpirationDate) {
        updateFormValues({ expiration_date: newExpirationDate });
      }
    }
  }, [formValues.contribution_date]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const requestData = {
        protector_id: formValues.protector_id!,
        contribution_date: formValues.contribution_date,
        expiration_date: formValues.expiration_date,
        currency_id: formValues.currency_id!,
        amount: parseFloat(formValues.amount),
        fixed_available_amount: parseFloat(formValues.fixed_available_amount),
        participant_shareholders_return: formValues.participant_shareholders_return,
        is_tax_deductible: formValues.is_tax_deductible,
        authorized_fund_id: formValues.authorized_fund_id!,
        previous_contribution_id: formValues.previous_contribution_id,
        minute_date: formValues.minute_date,
        minute_number: formValues.minute_number
      };

      const response = await createCapitalContribution(requestData);
      if (response.success) {
        if (id) {
          navigate(`/protectores/${id}`);
        } else {
          navigate(routes.fundsAndProtectors);
        }
      }
    } catch (error) {
      console.error('Error creating capital contribution:', error);
    } finally {
      setSubmitting(false);
    }
  };
  const currencyItems = metadata?.currencies?.map(c => ({
    label: c.name,
    disabled: false
  })) || [];

  const getSelectedCurrencyName = () => {
    const selected = metadata?.currencies?.find(c => c.id === formValues.currency_id);
    return selected ? selected.name : 'Seleccionar';
  };

  const handleCurrencyChange = (selectedLabel: string) => {
    const currency = metadata?.currencies?.find(c => c.name === selectedLabel);
    if (currency) {
      updateFormValues({ currency_id: currency.id });
    }
  };

  const participantShareholdersReturnItems = [
    { label: 'SI', disabled: false },
    { label: 'NO', disabled: false }
  ];

  const getParticipantShareholdersReturnLabel = () => {
    return formValues.participant_shareholders_return ? 'SI' : 'NO';
  };

  const handleParticipantShareholdersReturnChange = (selectedLabel: string) => {
    updateFormValues({ participant_shareholders_return: selectedLabel === 'SI' });
  };

  const isTaxDeductibleItems = [
    { label: 'SI', disabled: false },
    { label: 'NO', disabled: false }
  ];

  const getIsTaxDeductibleLabel = () => {
    return formValues.is_tax_deductible ? 'SI' : 'NO';
  };

  const handleIsTaxDeductibleChange = (selectedLabel: string) => {
    updateFormValues({ is_tax_deductible: selectedLabel === 'SI' });
  };

  const fundItems = metadata?.funds?.map(f => ({
    label: f.name,
    disabled: false
  })) || [];

  const getSelectedFundName = () => {
    const selected = metadata?.funds?.find(f => f.id === formValues.authorized_fund_id);
    return selected ? selected.name : 'Seleccionar';
  };

  const handleFundChange = (selectedLabel: string) => {
    const fund = metadata?.funds?.find(f => f.name === selectedLabel);
    if (fund) {
      updateFormValues({ authorized_fund_id: fund.id });
    }
  };

  const previousContributionItems = metadata?.previousContributions?.map(pc => ({
    label: pc.name,
    disabled: false
  })) || [];

  const getSelectedPreviousContributionName = () => {
    const selected = metadata?.previousContributions?.find(pc => pc.id === formValues.previous_contribution_id);
    return selected ? selected.name : 'Seleccionar';
  };

  const handlePreviousContributionChange = (selectedLabel: string) => {
    const previousContribution = metadata?.previousContributions?.find(pc => pc.name === selectedLabel);
    if (previousContribution) {
      updateFormValues({ previous_contribution_id: previousContribution.id });
    }
  };

  const isFormValid = () => {
    return (
      formValues.protector_id !== null &&
      formValues.contribution_date !== '' &&
      formValues.expiration_date !== '' &&
      formValues.currency_id !== null &&
      formValues.amount !== '' &&
      formValues.fixed_available_amount !== '' &&
      formValues.authorized_fund_id !== null &&
      formValues.minute_date !== '' &&
      formValues.minute_number !== ''
    );
  };

  if (loading) {
    return (
      <Card width='-webkit-fill-available'>
        <Card display='flex' justifyContent='center' alignItems='center' height='60vh'>
          <Spinner />
        </Card>
      </Card>
    );
  }

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      <Card margin={{ bottom: '4' }} display='flex' alignItems='center' gap='16px'>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a configuración"
        />
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          Nuevo aporte
        </H>
      </Card>

      <Card margin={{ bottom: '4' }}>
        <Card margin={{ bottom: '3' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong'>
            Datos del aporte
          </H>
        </Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
          <Input
            label="Monto *"
            value={formValues.amount}
            onChange={(e) => updateFormValues({ amount: e.target.value })}
            size="md"
            type="number"
          />
          <DropDown
            id="currency"
            label="Moneda *"
            title={getSelectedCurrencyName()}
            items={currencyItems}
            onChange={handleCurrencyChange}
            size="md"
            marginTop="4px"
          />
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de contribución *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.contribution_date}
                onChange={(date: string) => updateFormValues({ contribution_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de expiración *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.expiration_date}
                onChange={() => {}}
                disabled={true}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
          <Input
            label="Disponible fijo *"
            value={formValues.fixed_available_amount}
            onChange={(e) => updateFormValues({ fixed_available_amount: e.target.value })}
            size="md"
            type="number"
          />
          <DropDown
            id="participant_shareholders_return"
            label="Rendimiento socios partícipes *"
            title={getParticipantShareholdersReturnLabel()}
            items={participantShareholdersReturnItems}
            onChange={handleParticipantShareholdersReturnChange}
            size="md"
            marginTop="4px"
          />
          <DropDown
            id="is_tax_deductible"
            label="Desgrava Impositivamente *"
            title={getIsTaxDeductibleLabel()}
            items={isTaxDeductibleItems}
            onChange={handleIsTaxDeductibleChange}
            size="md"
            marginTop="4px"
          />
          <DropDown
            id="authorized_fund"
            label="Fondo autorizado *"
            title={getSelectedFundName()}
            items={fundItems}
            onChange={handleFundChange}
            size="md"
            marginTop="4px"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', alignItems: 'end' }}>
          <DropDown
            id="previous_contribution"
            label="Aporte anterior"
            title={getSelectedPreviousContributionName()}
            items={previousContributionItems}
            onChange={handlePreviousContributionChange}
            size="md"
            marginTop="4px"
          />
          <Input
            label="Número de acta *"
            value={formValues.minute_number}
            onChange={(e) => updateFormValues({ minute_number: e.target.value })}
            size="md"
          />
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de acta *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.minute_date}
                onChange={(date: string) => updateFormValues({ minute_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
        </div>
      </Card>

      <Card display='flex' justifyContent='flex-end' margin={{ top: '4' }}>
        <Button
          variant='primary'
          onClick={handleSubmit}
          disabled={submitting || !isFormValid()}
        >
          Crear aporte
        </Button>
      </Card>
    </Card>
  );
};

export default ProtectorContributionNew;
